import * as React from "react";
import {
	Box,
	Button,
	Container,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	makeStyles,
	MenuItem,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@material-ui/core";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import axiosWrapper from "common/axiosWrapper";
import DarkDataGrid from "common/components/DarkDataGrid/DarkDataGrid";
import { SetLoading } from "common/components/Loader/Loader";
import SettingsHelper from "common/helpers/settingsHelper";
import WebPushAdminService from "services/WebPushAdminService";

const AUTO_SETTING = "AUTOMATIC_NOTIFICATIONS_ENABLED";
const REMINDER_SETTING = "PICK_REMINDER_ENABLED";

const useStyles = makeStyles((theme) => ({
	paper: {
		padding: theme.spacing(4),
		marginBottom: theme.spacing(4),
	},
	field: {
		marginBottom: theme.spacing(3),
		minWidth: 200,
	},
	bigNumber: {
		fontSize: 28,
		fontWeight: 600,
	},
	gridWrap: { height: 520, width: "100%" },
}));

const utc = (value) => (value ? value.replace("T", " ").slice(0, 16) : "-");

const historyColumns = [
	{ field: "sentAt", headerName: "Sent (UTC)", width: 150, valueFormatter: (p) => utc(p.value) },
	{ field: "type", headerName: "Type", width: 140 },
	{ field: "eventId", headerName: "Event", width: 90 },
	{ field: "audience", headerName: "Audience", width: 120 },
	{ field: "attemptedCount", headerName: "Sent to", width: 100, type: "number" },
	{ field: "deliveredCount", headerName: "Delivered", width: 110, type: "number" },
	{ field: "title", headerName: "Title", flex: 1, minWidth: 160 },
];

function useSettingToggle(settingName) {
	const [enabled, setEnabled] = React.useState(false);
	const [ready, setReady] = React.useState(false);

	React.useEffect(() => {
		SettingsHelper.getSetting(settingName).then((value) => {
			setEnabled(value === true);
			setReady(true);
		});
	}, [settingName]);

	const onToggle = async (e) => {
		const next = e.target.checked;
		setEnabled(next);
		SetLoading(true, "Saving...");
		try {
			await axiosWrapper.put(`api/settings/${settingName}`, {
				value: next ? "true" : "false",
			});
			SettingsHelper.settings.set(settingName, next);
		} catch (err) {
			setEnabled(!next);
			toast("Save failed", { type: "error" });
		} finally {
			SetLoading(false);
		}
	};

	return { enabled, ready, onToggle };
}

function parseUserIds(raw) {
	return raw
		.split(",")
		.map((s) => parseInt(s.trim(), 10))
		.filter((n) => Number.isInteger(n) && n > 0);
}

export default function AdminNotifications() {
	const classes = useStyles();
	const [stats, setStats] = React.useState(null);
	const [history, setHistory] = React.useState([]);
	const [mode, setMode] = React.useState("all");
	const [gameId, setGameId] = React.useState("");
	const [userIdsRaw, setUserIdsRaw] = React.useState("");
	const [title, setTitle] = React.useState("");
	const [body, setBody] = React.useState("");
	const [url, setUrl] = React.useState("");
	const [sending, setSending] = React.useState(false);
	const auto = useSettingToggle(AUTO_SETTING);
	const reminder = useSettingToggle(REMINDER_SETTING);

	const loadStats = React.useCallback(() => {
		SetLoading(true, "Loading stats");
		WebPushAdminService.getStats()
			.then((s) => setStats(s))
			.catch(() => toast("Could not load stats", { type: "error" }))
			.finally(() => SetLoading(false));
	}, []);

	const loadHistory = React.useCallback(() => {
		WebPushAdminService.getHistory()
			.then((h) => setHistory(Array.isArray(h) ? h : []))
			.catch(() => toast("Could not load history", { type: "error" }));
	}, []);

	React.useEffect(() => {
		loadStats();
		loadHistory();
	}, [loadStats, loadHistory]);

	const canSend =
		!sending &&
		title.trim().length > 0 &&
		body.trim().length > 0 &&
		(mode === "all" ||
			(mode === "game" && gameId !== "") ||
			(mode === "users" && parseUserIds(userIdsRaw).length > 0));

	const send = async () => {
		const payload = { mode, title, body, url: url || null };
		if (mode === "game") payload.gameId = gameId;
		if (mode === "users") payload.userIds = parseUserIds(userIdsRaw);

		const targetLabel =
			mode === "all"
				? "ALL subscribers"
				: mode === "game"
				? `game ${gameId}`
				: `${payload.userIds.length} user(s)`;
		if (!window.confirm(`Send notification to ${targetLabel}?`)) return;

		setSending(true);
		try {
			const result = await WebPushAdminService.send(payload);
			toast(`Sent to ${result.recipientCount} subscription(s)`, { type: "success" });
			loadStats();
			loadHistory();
		} catch (e) {
			toast("Send failed", { type: "error" });
		} finally {
			setSending(false);
		}
	};

	return (
		<Container maxWidth="lg">
			<Helmet>
				<title>Pickstop - Admin Notifications</title>
			</Helmet>

			<Box mt={4}>
				<Paper className={classes.paper} elevation={3}>
					<Typography variant="h6" gutterBottom>
						Automatic notifications
					</Typography>
					<FormControlLabel
						control={
							<Switch
								checked={auto.enabled}
								onChange={auto.onToggle}
								disabled={!auto.ready}
								color="primary"
							/>
						}
						label="Send push notifications when an event or stage opens"
					/>
					<FormControlLabel
						control={
							<Switch
								checked={reminder.enabled}
								onChange={reminder.onToggle}
								disabled={!reminder.ready}
								color="primary"
							/>
						}
						label="Remind returning players an hour before picks lock"
					/>
				</Paper>

				<Paper className={classes.paper} elevation={3}>
					<Typography variant="h6" gutterBottom>
						Web Push stats
					</Typography>
					{stats ? (
						<>
							<Grid container spacing={4}>
								<Grid item xs={6} sm={3}>
									<Typography variant="caption">Distinct users</Typography>
									<Typography className={classes.bigNumber}>
										{stats.distinctUsers}
									</Typography>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Typography variant="caption">Distinct browsers</Typography>
									<Typography className={classes.bigNumber}>
										{stats.distinctEndpoints}
									</Typography>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Typography variant="caption">Total subscriptions</Typography>
									<Typography className={classes.bigNumber}>
										{stats.totalSubscriptions}
									</Typography>
								</Grid>
							</Grid>
							<Box mt={4}>
								<Typography variant="subtitle2">Per game</Typography>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Game</TableCell>
											<TableCell align="right">Distinct users</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{stats.perGame.map((g) => (
											<TableRow key={g.gameId}>
												<TableCell>{g.gameName}</TableCell>
												<TableCell align="right">{g.distinctUsers}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</>
					) : (
						<Typography variant="body2">No stats loaded.</Typography>
					)}
				</Paper>

				<Paper className={classes.paper} elevation={3}>
					<Typography variant="h6" gutterBottom>
						Send notification
					</Typography>

					<FormControl className={classes.field}>
						<InputLabel>Target</InputLabel>
						<Select
							value={mode}
							onChange={(e) => setMode(e.target.value)}
						>
							<MenuItem value="all">All subscribers</MenuItem>
							<MenuItem value="game">By game</MenuItem>
							<MenuItem value="users">By user IDs</MenuItem>
						</Select>
					</FormControl>

					{mode === "game" && (
						<FormControl className={classes.field} fullWidth>
							<InputLabel>Game</InputLabel>
							<Select
								value={gameId}
								onChange={(e) => setGameId(e.target.value)}
							>
								{(stats?.perGame ?? []).map((g) => (
									<MenuItem key={g.gameId} value={g.gameId}>
										{g.gameName} ({g.distinctUsers} users)
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}

					{mode === "users" && (
						<TextField
							className={classes.field}
							label="User IDs (comma-separated)"
							placeholder="42, 1337, 9001"
							value={userIdsRaw}
							onChange={(e) => setUserIdsRaw(e.target.value)}
							fullWidth
						/>
					)}

					<TextField
						className={classes.field}
						label="Title"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						required
						fullWidth
					/>
					<TextField
						className={classes.field}
						label="Body"
						value={body}
						onChange={(e) => setBody(e.target.value)}
						required
						multiline
						minRows={2}
						fullWidth
					/>
					<TextField
						className={classes.field}
						label="URL (optional, e.g. /?event=42)"
						value={url}
						onChange={(e) => setUrl(e.target.value)}
						fullWidth
					/>

					<Button
						variant="contained"
						color="primary"
						disabled={!canSend}
						onClick={send}
					>
						{sending ? "Sending..." : "Send"}
					</Button>
				</Paper>

				<Paper className={classes.paper} elevation={3}>
					<Typography variant="h6" gutterBottom>
						Past notifications
					</Typography>
					<Typography variant="body2" gutterBottom>
						The 200 most recent sends, automatic and manual.
					</Typography>
					<div className={classes.gridWrap}>
						<DarkDataGrid
							rows={history}
							columns={historyColumns}
							pageSize={25}
							rowsPerPageOptions={[25, 50, 100]}
							disableSelectionOnClick
						/>
					</div>
				</Paper>
			</Box>
		</Container>
	);
}
